import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Loader2, Save, ArrowRight, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useBracket } from '@/hooks/use-bracket';
import BracketGrid from '@/components/BracketGrid';
import Layout from '@/components/Layout';
import { BracketPick } from '@/types/bracket';
import BracketShareButton from '@/components/BracketShareButton';
import { trackEvent } from '@/lib/analytics';

const BracketPage = () => {
  const { userId: viewUserIdParam } = useParams<{ userId?: string }>();
  const { user } = useAuth();

  // If URL has a :userId param and it differs from auth user, we're viewing someone else
  const viewUserId = viewUserIdParam !== user?.id ? viewUserIdParam : undefined;

  const {
    picks,
    setPick,
    savePicks,
    oddsMap,
    bracketSlots,
    loading,
    saving,
    isLocked,
    isViewingOther,
    filledSlots,
    totalSlots,
  } = useBracket(viewUserId);

  const handlePickWinner = (slotId: string, winner: string) => {
    const existing = picks[slotId];
    // Preserve predicted_score and odds if same winner re-selected
    if (existing?.winner === winner) {
      // Deselect
      setPick(slotId, null);
      return;
    }
    // When changing winner, clear score (different winner = different odds)
    const newPick: BracketPick = {
      winner,
      predicted_score: null,
      odds_at_bet: null,
    };
    setPick(slotId, newPick);
  };

  const handleSavePicks = async () => {
    await savePicks();
    trackEvent('bracket_filled');
  };

  const handlePickScore = (slotId: string, score: string, americanOdds: number | null, winner: string) => {
    setPick(slotId, {
      winner,
      predicted_score: score,
      odds_at_bet: americanOdds,
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 pb-32">

        {/* Page header */}
        <div className="flex flex-col gap-2 mb-2 md:flex-row md:items-center md:justify-between">
          {/* Line 1: Back link + Global leaderboard */}
          <div className="flex items-center justify-between w-full md:w-auto md:order-first">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
              <ArrowRight className="w-4 h-4 rotate-180" /> חזרה לדשבורד
            </Link>
            <Link to="/bracket-leaderboard" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
              <Trophy className="w-4 h-4" /> דירוג גלובלי
            </Link>
          </div>
          {/* Line 2: Title */}
          <h1 className="text-2xl font-bold tracking-tight text-center md:text-right">
            {isViewingOther ? 'ברקט שחקן' : 'GLOBAL BRACKET'}
          </h1>
          {/* Line 3: Progress pill + Share button */}
          <div className="flex items-center justify-between md:justify-end md:gap-3">
            {/* Progress pill */}
            <div className="bg-card border border-border rounded-full px-4 py-1.5 text-sm font-medium">
              <span className={filledSlots === totalSlots ? 'text-accent' : 'text-muted-foreground'}>
                {filledSlots}/{totalSlots}
              </span>
              <span className="text-muted-foreground text-xs ml-1">בחירות</span>
            </div>
            {/* Share button — own bracket only */}
            {!isViewingOther && !loading && (
              <BracketShareButton picks={picks} bracketSlots={bracketSlots} />
            )}
          </div>
        </div>

        {/* Locked banner */}
        {isLocked && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-destructive/10 border border-destructive/30 rounded-2xl p-4 mb-6 flex items-center gap-3"
          >
            <Lock className="w-5 h-5 text-destructive shrink-0" />
            <p className="text-sm text-destructive font-medium">
              הברקט ננעל — לא ניתן לערוך
            </p>
          </motion.div>
        )}

        {/* Bracket */}
        <BracketGrid
          bracketSlots={bracketSlots}
          picks={picks}
          oddsMap={oddsMap}
          onPickWinner={handlePickWinner}
          onPickScore={handlePickScore}
          onClearPick={(slotId) => setPick(slotId, null)}
          isLocked={isLocked}
          isViewingOther={!!isViewingOther}
        />
      </div>

      {/* Sticky save bar — only shown when not locked and not viewing other */}
      {!isLocked && !isViewingOther && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-card/90 backdrop-blur-md border-t border-border p-4 z-50"
        >
          <div className="container mx-auto max-w-4xl flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">
                {filledSlots === totalSlots
                  ? 'כל הבחירות מלאות!'
                  : `עוד ${totalSlots - filledSlots} בחירות`}
              </p>
              <p className="text-xs text-muted-foreground">נועל ב-18 באפריל</p>
            </div>
            <Button
              onClick={handleSavePicks}
              disabled={saving || filledSlots === 0}
              className="bg-accent hover:bg-accent/90 text-accent-foreground btn-accent-glow"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin ml-2" /> שומר...</>
              ) : (
                <><Save className="w-4 h-4 ml-2" /> שמור ברקט</>
              )}
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default BracketPage;
